import { createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode } from "react";
import { useRadioTracking } from "@/hooks/useRadioTracking";
import { useRadioMediaSession } from "@/hooks/useRadioMediaSession";
import { subscribeCallActive } from "@/lib/call-audio-coordinator";


export interface RadioStation {
  stationuuid: string;
  name: string;
  url_resolved: string;
  favicon: string;
  country: string;
  countrycode: string;
  tags: string;
  votes: number;
  bitrate: number;
  language: string;
}

interface AudioOutputDevice {
  deviceId: string;
  label: string;
}

interface RadioContextType {
  // Playback
  currentStation: RadioStation | null;
  isPlaying: boolean;
  isMuted: boolean;
  volume: number;
  isBuffering: boolean;
  error: string | null;
  play: (station: RadioStation) => void;
  stop: () => void;
  toggleMute: () => void;
  setVolume: (v: number) => void;
  // Audio output
  outputDevices: AudioOutputDevice[];
  currentOutputId: string;
  selectOutput: (deviceId: string) => Promise<void>;
  refreshOutputDevices: () => Promise<void>;
  // Search
  searchStations: (query: string) => Promise<RadioStation[]>;
  isSearching: boolean;
  markConversion: (type: 'signup' | 'topup' | 'call') => void;
  lastSearchQuery: string;
}

const RadioContext = createContext<RadioContextType | null>(null);

export function useRadio() {
  const ctx = useContext(RadioContext);
  if (!ctx) throw new Error("useRadio must be used within RadioProvider");
  return ctx;
}

const STORAGE_KEY = "elefa-radio-last-station";
const RADIO_PROXY = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/radio-proxy`;

function saveLastStation(station: RadioStation) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(station));
  } catch { /* quota */ }
}

export function getLastStation(): RadioStation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function RadioProvider({ children }: { children: ReactNode }) {
  const [currentStation, setCurrentStation] = useState<RadioStation | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolumeState] = useState(1);
  const [isBuffering, setIsBuffering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outputDevices, setOutputDevices] = useState<AudioOutputDevice[]>([]);
  const [currentOutputId, setCurrentOutputId] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentStationRef = useRef<RadioStation | null>(null);
  const userStoppedRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const { startSession, endSession, markConversion } = useRadioTracking();

  // Keep ref in sync so listeners (registered once) always see the latest station
  useEffect(() => { currentStationRef.current = currentStation; }, [currentStation]);

  // Auto-reconnect when stream stalls/ends/errors mid-playback. Live radio
  // streams routinely drop after 30-60s on flaky networks or proxy hiccups;
  // a healthy radio app silently resumes instead of going dead.
  const scheduleReconnect = useCallback(() => {
    if (userStoppedRef.current) return;
    const station = currentStationRef.current;
    const audio = audioRef.current;
    if (!station || !audio) return;
    if (reconnectTimerRef.current) return;
    const attempt = reconnectAttemptsRef.current;
    // Exponential backoff capped at 8s. Reset on successful "playing".
    const delay = Math.min(800 * Math.pow(1.7, attempt), 8000);
    reconnectAttemptsRef.current = attempt + 1;
    setIsBuffering(true);
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      const a = audioRef.current;
      const s = currentStationRef.current;
      if (!a || !s || userStoppedRef.current) return;
      try {
        const url = buildPlayableUrl(s.url_resolved);
        // Cache-bust to defeat any stale CDN/proxy connection
        const bust = url + (url.includes('?') ? '&' : '?') + '_r=' + Date.now();
        a.src = bust;
        a.crossOrigin = "anonymous";
        a.volume = isMuted ? 0 : volume;
        a.play().catch(() => scheduleReconnect());
      } catch {
        scheduleReconnect();
      }
    }, delay) as unknown as number;
  // buildPlayableUrl/isMuted/volume captured via refs-of-state would be ideal,
  // but these change rarely; including them is safe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create a persistent audio element once
  useEffect(() => {
    const audio = new Audio();
    // `auto` keeps the media session warm so iOS WKWebView doesn't tear the
    // AVAudioSession down when the screen locks. With UIBackgroundModes=audio
    // (set by patch-ios-voip-capabilities.mjs) this lets playback continue
    // on the lock screen indefinitely - matches Spotify / Apple Music.
    audio.preload = "auto";
    // iOS WKWebView: required so the OS treats the element as a true
    // background-eligible media item rather than an inline page video.
    audio.setAttribute("playsinline", "");
    audio.setAttribute("webkit-playsinline", "");
    audio.setAttribute("x-webkit-airplay", "allow");
    audioRef.current = audio;

    const onPlaying = () => {
      reconnectAttemptsRef.current = 0;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      setIsPlaying(true);
      setIsBuffering(false);
      setError(null);
    };
    const onWaiting = () => setIsBuffering(true);
    const onPause = () => {
      // CRITICAL: Do NOT auto-reconnect on every pause event. iOS WKWebView
      // fires `pause` when the screen locks even though it then immediately
      // resumes the AVAudioSession in the background; our previous reconnect
      // tore the source down and made it look like radio "stops on lock".
      //
      // We only treat this as a real drop when the element is actually
      // unable to resume - `stalled`/`ended`/`error` handle that path.
      setIsPlaying(false);
    };
    const onEnded = () => {
      // Live streams should never "end"; treat as a drop and reconnect.
      if (!userStoppedRef.current && currentStationRef.current) {
        scheduleReconnect();
      }
    };
    const onStalled = () => {
      if (!userStoppedRef.current && currentStationRef.current) {
        scheduleReconnect();
      }
    };
    const onError = () => {
      if (!userStoppedRef.current && currentStationRef.current) {
        scheduleReconnect();
      } else {
        setIsPlaying(false);
        setIsBuffering(false);
        setError("Stream unavailable. Try another station.");
      }
    };

    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("stalled", onStalled);
    // `suspend` fires every time the buffer is full on iOS - it is NOT a
    // failure. Listening to it caused spurious reconnects that killed
    // background playback. Removed deliberately.
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("stalled", onStalled);
      audio.removeEventListener("error", onError);
      audio.pause();
      audio.src = "";
      audio.load();
    };
  }, [scheduleReconnect]);

  // Hold a Screen Wake Lock while playing so Android WebView keeps the
  // media element resident when the user locks the screen. Released the
  // moment the user stops. Silently no-ops on iOS (uses background audio
  // mode instead) and on browsers without the API.
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const nav = navigator as Navigator & { wakeLock?: { request: (t: string) => Promise<{ release: () => Promise<void>; addEventListener: (e: string, cb: () => void) => void }> } };
    if (!nav.wakeLock?.request) return;
    if (!isPlaying || !currentStation) return;

    let lock: { release: () => Promise<void>; addEventListener: (e: string, cb: () => void) => void } | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        const l = await nav.wakeLock!.request("screen");
        if (cancelled) { l.release().catch(() => {}); return; }
        lock = l;
        // Re-acquire if the OS revokes it (tab backgrounded, screen off, etc.)
        l.addEventListener("release", () => {
          if (!cancelled && isPlaying) acquire();
        });
      } catch { /* permission/policy denied - non-fatal */ }
    };
    acquire();

    // Also re-acquire whenever the document becomes visible again.
    const onVis = () => {
      if (document.visibilityState === "visible" && isPlaying && !lock) acquire();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      lock?.release().catch(() => {});
      lock = null;
    };
  }, [isPlaying, currentStation]);

  // Build a playable URL: upgrade http→https where possible, otherwise route via edge proxy
  const buildPlayableUrl = useCallback((rawUrl: string): string => {
    if (!rawUrl) return rawUrl;
    const isHttps = rawUrl.startsWith('https://');
    const isHttp = rawUrl.startsWith('http://');
    const isPagePlaintext = typeof window !== 'undefined' && window.location.protocol === 'http:';
    if (isHttps || isPagePlaintext) return rawUrl;
    if (isHttp) {
      // Mixed-content blocked → route through HTTPS edge proxy
      return `${RADIO_PROXY}?type=stream&u=${encodeURIComponent(rawUrl)}`;
    }
    return rawUrl;
  }, []);

  const play = useCallback((station: RadioStation) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Same station + currently playing → fully stop (not just pause).
    // Pausing alone leaves the audio element wired up; iOS lock-screen /
    // MediaSession can then auto-resume in the background without the app
    // showing it as playing. Always tear the source down on user pause.
    if (currentStation?.stationuuid === station.stationuuid && !audio.paused) {
      stop();
      return;
    }

    // User-initiated playback - clear any pending reconnect/stop guard
    userStoppedRef.current = false;
    reconnectAttemptsRef.current = 0;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Unlock audio element immediately within user gesture context (iOS/Safari)
    audio.pause();
    audio.src = "";
    audio.load();

    // End previous session
    endSession();

    setError(null);
    setIsBuffering(true);
    setCurrentStation(station);
    saveLastStation(station);

    // Track new session
    startSession(station, lastSearchQuery);

    const primaryUrl = buildPlayableUrl(station.url_resolved);
    const proxyFallback = `${RADIO_PROXY}?type=stream&u=${encodeURIComponent(station.url_resolved)}`;
    let triedFallback = primaryUrl === proxyFallback;

    const attempt = (src: string) => {
      audio.src = src;
      audio.crossOrigin = "anonymous";
      audio.volume = isMuted ? 0 : volume;
      audio.play().catch(() => {
        if (!triedFallback) {
          triedFallback = true;
          attempt(proxyFallback);
        } else {
          setError("Could not play this station. Try another.");
          setIsBuffering(false);
        }
      });
    };

    // One-shot error handler for stream failures (e.g. 404, ICY, TLS)
    const onErrorOnce = () => {
      if (!triedFallback) {
        triedFallback = true;
        attempt(proxyFallback);
      }
    };
    audio.addEventListener('error', onErrorOnce, { once: true });

    attempt(primaryUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStation, isMuted, volume, endSession, startSession, lastSearchQuery, buildPlayableUrl]);

  const stop = useCallback(() => {
    userStoppedRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = "";
      try { audio.load(); } catch { /* noop */ }
    }
    // Tear the MediaSession down so iOS / Android cannot auto-resume from
    // the lock screen or control center after the user has stopped playback.
    if (typeof navigator !== "undefined" && "mediaSession" in navigator) {
      try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = "none";
        navigator.mediaSession.setActionHandler("play", null);
        navigator.mediaSession.setActionHandler("pause", null);
        navigator.mediaSession.setActionHandler("stop", null);
      } catch { /* noop */ }
    }
    endSession();
    setCurrentStation(null);
    setIsPlaying(false);
    setIsBuffering(false);
  }, [endSession]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev;
      if (audioRef.current) {
        audioRef.current.volume = next ? 0 : volume;
      }
      return next;
    });
  }, [volume]);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : v;
    }
  }, [isMuted]);

  // Audio output device management
  const refreshOutputDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outputs = devices
        .filter(d => d.kind === "audiooutput")
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Speaker ${d.deviceId.slice(0, 4)}`,
        }));
      setOutputDevices(outputs);
    } catch { /* permission denied */ }
  }, []);

  const selectOutput = useCallback(async (deviceId: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if ("setSinkId" in audio) {
        await (audio as any).setSinkId(deviceId);
        setCurrentOutputId(deviceId);
      }
    } catch (err) {
      console.warn("[Radio] Failed to set output device:", err);
    }
  }, []);

  // Search worldwide
  const searchStations = useCallback(async (query: string): Promise<RadioStation[]> => {
    if (!query || query.length < 2) return [];
    setIsSearching(true);
    setLastSearchQuery(query);
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const res = await fetch(
        `${RADIO_PROXY}?type=search&q=${encodeURIComponent(query)}&limit=30`,
        { signal: controller.signal }
      );
      clearTimeout(timeout);
      if (!res.ok) return [];
      const data: RadioStation[] = await res.json();
      if (Array.isArray(data)) return data.filter(s => s.url_resolved);
      return [];
    } catch {
      return [];
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Lock-screen / Now Playing artwork animation
  const resumePlay = useCallback(() => {
    const audio = audioRef.current;
    if (audio && currentStation) audio.play().catch(() => {});
  }, [currentStation]);
  const pausePlay = useCallback(() => {
    audioRef.current?.pause();
  }, []);
  useRadioMediaSession(currentStation, isPlaying, stop, pausePlay, resumePlay);

  // Auto-stop on any incoming/active call. `stop()` (not pause) so the
  // userStoppedRef guard kicks in and the stream cannot silently auto-resume
  // via MediaSession / reconnect once the call ends.
  useEffect(() => {
    return subscribeCallActive((active) => {
      if (active && audioRef.current && !audioRef.current.paused) {
        stop();
      }
    });
  }, [stop]);


  // Listen for device changes
  useEffect(() => {
    refreshOutputDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", refreshOutputDevices);
    return () => {
      navigator.mediaDevices?.removeEventListener?.("devicechange", refreshOutputDevices);
    };
  }, [refreshOutputDevices]);

  return (
    <RadioContext.Provider value={{
      currentStation,
      isPlaying,
      isMuted,
      volume,
      isBuffering,
      error,
      play,
      stop,
      toggleMute,
      setVolume,
      outputDevices,
      currentOutputId,
      selectOutput,
      refreshOutputDevices,
      searchStations,
      isSearching,
      markConversion,
      lastSearchQuery,
    }}>
      {children}
    </RadioContext.Provider>
  );
}
